import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Check, ClipboardCheck, X } from "lucide-react";
import { Button } from "@ui/components/ui/button";
import { Badge } from "@ui/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@ui/components/ui/card";
import {
  getListBotSubmissionsQueryKey,
  useListBotSubmissions,
  useReviewBotSubmission,
} from "@tubo/api-models/hooks/bot-admin-api.hooks";
import { getListBotMarketListingsQueryKey } from "@tubo/api-models/hooks/bot-market-api.hooks";

export const Route = createFileRoute("/_app/tenant-settings/bots")({
  component: TenantBotSettingsPage,
});

/** 企业 Bot 治理：发布申请审核。（自定义工具管理已收编至智能体工坊 /bots → 工具 tab） */
function TenantBotSettingsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useListBotSubmissions();
  const submissions = data?.listings ?? [];
  const reviewMutation = useReviewBotSubmission({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListBotSubmissionsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListBotMarketListingsQueryKey() });
      },
    },
  });

  const review = (id: string, approved: boolean) => {
    reviewMutation.mutate({ id, data: { approved } });
  };

  return (
    <div className="mx-auto max-w-5xl p-6 md:p-8">
      <header className="mb-6">
        <div className="mb-3 flex items-center gap-2 text-primary">
          <ClipboardCheck className="size-5" />
          <span className="text-sm font-medium">企业 Bot 治理</span>
        </div>
        <div className="mb-4">
          <h1 className="text-2xl font-semibold">待审核发布申请</h1>
          <p className="mt-2 text-sm text-muted-foreground">审核通过后，成员将只能使用此申请绑定的不可变版本。</p>
        </div>
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">正在加载发布申请…</p>}
      {!isLoading && submissions.length === 0 && (
        <div className="rounded-xl border border-dashed p-12 text-center text-sm text-muted-foreground">
          暂无待审核的 Bot 发布申请。
        </div>
      )}
      <div className="space-y-3">
        {submissions.map((submission) => (
          <Card key={submission.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-base">{submission.name}</CardTitle>
                  <CardDescription className="mt-1">版本 {submission.version} · 等待企业审核</CardDescription>
                </div>
                <Badge variant="outline">待审核</Badge>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm text-muted-foreground">{submission.desc || "暂未填写说明"}</p>
                {submission.releaseNotes && <p className="mt-2 text-xs text-muted-foreground">发布说明：{submission.releaseNotes}</p>}
              </div>
              <div className="flex shrink-0 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => review(submission.id, false)}
                  disabled={reviewMutation.isPending}
                >
                  <X className="size-4" />
                  驳回
                </Button>
                <Button
                  size="sm"
                  onClick={() => review(submission.id, true)}
                  disabled={reviewMutation.isPending}
                >
                  <Check className="size-4" />
                  通过
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
